import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { formatPhoneNumber, isValidEmail } from "../../../utils/formatUtils";
import { capitalizeFirstLetter } from "../../../utils/capitalize";
import {
  prepareAscellaReferral,
  submitAscellaReferral,
} from "../../../api/ascella";

// Import Modular Components
import AscellaMemberDetailsHeader from "./AscellaMemberDetailsHeader";
import PatientDemographics from "./PatientDemographics";
import PrescriberInfo from "./PrescriberInfo";
import BenefitsCoordination from "./BenefitsCoordination";
import CopayAssistanceAndDiagnosis from "./CopayAssistanceAndDiagnosis";
import MedicationPrescribed from "./MedicationPrescribed";
import CommonDialog from "../../common/CommonDialog";

// --- Validation & Formatting Helpers ---
const formatISO = (dateVal) => {
  if (!dateVal) return "";
  const date = new Date(dateVal);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toISOString().split(".")[0] + "Z";
};

const parseDouble = (val) => {
  const num = Number.parseFloat(val);
  return Number.isNaN(num) ? "0.0" : num;
};

function CustomStepIcon(props) {
  const { active, completed } = props;

  let borderStyle;
  if (completed) {
    borderStyle = "none";
  } else if (active) {
    borderStyle = "3.5px solid #403C6F";
  } else {
    borderStyle = "2px solid #C4C2D3";
  }
  return (
    <Box
      sx={{
        width: 14,
        height: 14,
        boxSizing: "border-box",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: completed ? "#403C6F" : "#F7F8FB",
        border: borderStyle,
        zIndex: 1,
      }}
    >
      {completed && (
        <CheckIcon
          sx={{
            color: "#FFFFFF",
            fontSize: "10px",
            stroke: "#FFFFFF",
            strokeWidth: 1,
          }}
        />
      )}
    </Box>
  );
}

CustomStepIcon.propTypes = {
  active: PropTypes.bool,
  completed: PropTypes.bool,
};

const steps = [
  "Patient & Prescriber Info",
  "Insurance & Copay Assistance",
  "Medication Details",
];

const getInitialFormData = (
  opportunity,
  apiResponse = {},
  memberData = {},
  copayCardDetails = {},
) => {
  const member = opportunity?.member || {};
  const prescriber = opportunity?.provider || {};
  const medication = opportunity?.medication || {};
  const program = opportunity?.program || {};

  let quantityVal = 0;
  if (apiResponse?.medicationPrescribed?.quantity !== undefined) {
    quantityVal = Number(apiResponse.medicationPrescribed.quantity);
  } else if (program.metricQty !== undefined) {
    quantityVal = Number(program.metricQty);
  }

  let daysSupplyVal = 0;
  if (apiResponse?.medicationPrescribed?.daysSupply !== undefined) {
    daysSupplyVal = Number(apiResponse.medicationPrescribed.daysSupply);
  } else if (program.daySupply !== undefined) {
    daysSupplyVal = Number(program.daySupply);
  }

  let copayAssistanceAmountVal = 0;
  if (apiResponse?.copayAssistance?.copayAssistanceAmount !== undefined) {
    copayAssistanceAmountVal = Number(
      apiResponse.copayAssistance.copayAssistanceAmount,
    );
  } else if (opportunity.copayAssistanceAmount !== undefined) {
    copayAssistanceAmountVal = Number(opportunity.copayAssistanceAmount);
  }

  // Parse patient name
  const fullName = (
    memberData?.name ||
    member.fullName ||
    member.name ||
    ""
  ).trim();
  const nameSplit = fullName ? fullName.split(" ").filter(Boolean) : [];
  const firstName =
    apiResponse?.patient?.firstName ||
    memberData?.firstName ||
    member.firstName ||
    nameSplit[0] ||
    ""; // "John"
  const lastName =
    apiResponse?.patient?.lastName ||
    memberData?.lastName ||
    member.lastName ||
    nameSplit.slice(1).join(" ") ||
    ""; // "Doe"

  // Parse prescriber name
  const docName = (prescriber.name || "").trim();
  const docNameSplit = docName ? docName.split(" ").filter(Boolean) : [];
  const docFirstName =
    apiResponse?.prescriber?.firstName ||
    prescriber.firstName ||
    docNameSplit[0] ||
    ""; // "Jane"
  const docLastName =
    apiResponse?.prescriber?.lastName ||
    prescriber.lastName ||
    docNameSplit.slice(1).join(" ") ||
    ""; // "Smith"

  // Address
  const rawAddress = memberData?.address || member.address || {};
  const addressLine1 =
    apiResponse?.patient?.addressLine1 ||
    rawAddress.line1 ||
    rawAddress.street ||
    memberData?.street ||
    "";
  const city =
    apiResponse?.patient?.city || rawAddress.city || memberData?.city || "";
  const stateProvince =
    apiResponse?.patient?.stateProvince ||
    rawAddress.state ||
    memberData?.state ||
    "";
  const postalCode =
    apiResponse?.patient?.postalCode ||
    rawAddress.postalCode ||
    rawAddress.zip ||
    memberData?.zip ||
    memberData?.postalCode ||
    "";

  return {
    clientAccountId:
      apiResponse?.clientAccountId || memberData?.clientAccountId || "", // "78910"
    patient: {
      firstName: capitalizeFirstLetter(firstName),
      lastName: capitalizeFirstLetter(lastName),
      dateOfBirth:
        apiResponse?.patient?.dateOfBirth ||
        memberData?.dateOfBirth ||
        memberData?.dob ||
        member.dateOfBirth ||
        member.dob ||
        "", // "1985-05-15"
      gender:
        apiResponse?.patient?.gender ||
        memberData?.gender ||
        memberData?.sex ||
        member.sex ||
        member.gender ||
        "", // "Male"
      addressLine1: capitalizeFirstLetter(addressLine1),
      addressLine2: capitalizeFirstLetter(
        apiResponse?.patient?.addressLine2 ||
          rawAddress.line2 ||
          memberData?.line2 ||
          memberData?.addressLine2 ||
          "",
      ),
      city: capitalizeFirstLetter(city),
      stateProvince: stateProvince,
      postalCode: postalCode,
      countryCode:
        apiResponse?.patient?.countryCode ||
        rawAddress.country ||
        memberData?.country ||
        "US", // "US"
      phone:
        apiResponse?.patient?.phone ||
        memberData?.phone ||
        memberData?.phoneNumber ||
        memberData?.preferredPhoneNumber ||
        member.phoneNumber ||
        member.phone ||
        "", // "555-0199"
      email:
        apiResponse?.patient?.email ||
        memberData?.email ||
        member.email ||
        member.contact?.email ||
        "", // "johndoe@example.com"
      phoneSupportsSms:
        apiResponse?.patient?.phoneSupportsSms ??
        memberData?.phoneSupportsSms ??
        member?.phoneSupportsSms ??
        false,
    },
    benefitsCoordination: {
      iinNumber:
        apiResponse?.benefitsCoordination?.iinNumber ||
        memberData?.bin ||
        opportunity.bin ||
        "", // "023385"
      processorIdentificationNumber:
        apiResponse?.benefitsCoordination?.processorIdentificationNumber ||
        memberData?.pcn ||
        opportunity.pcn ||
        "", // "123456789"
      cardHolderID:
        apiResponse?.benefitsCoordination?.cardHolderID ||
        memberData?.subscriberNum ||
        member.insuranceMemberIdentifier ||
        member.subscriberNum ||
        "", // "XY1234567"
      personCode:
        apiResponse?.benefitsCoordination?.personCode ||
        memberData?.personCode ||
        member.insuranceRelationshipCode ||
        "", // "01"
      groupID:
        apiResponse?.benefitsCoordination?.groupID ||
        memberData?.groupNum ||
        member.groupId ||
        "", // "GRP999"
      groupName: capitalizeFirstLetter(
        apiResponse?.benefitsCoordination?.groupName ||
          memberData?.groupName ||
          member.groupName ||
          "",
      ), // "Standard Plan"
      benefitStartDate:
        apiResponse?.benefitsCoordination?.benefitStartDate ||
        memberData?.benefitStartDate ||
        member.benefitStartDate ||
        "", // "2026-01-01"
      phone:
        apiResponse?.benefitsCoordination?.phone ||
        memberData?.benefitsPhone ||
        member.benefitsPhone ||
        "", // "800-555-0122"
    },
    medicationPrescribed: {
      rxNumber:
        apiResponse?.medicationPrescribed?.rxNumber ||
        opportunity.rxNumber ||
        "",
      priorAuthNumber:
        apiResponse?.medicationPrescribed?.priorAuthNumber ||
        opportunity.priorAuthNumber ||
        "",
      writtenDate:
        apiResponse?.medicationPrescribed?.writtenDate ||
        opportunity.writtenDate ||
        "",
      drugName: capitalizeFirstLetter(
        apiResponse?.medicationPrescribed?.drugName ||
          medication.drugName ||
          "",
      ), // "HUMIRA 40 MG/0.8 ML PEN"
      ndc:
        apiResponse?.medicationPrescribed?.ndc ||
        medication.drugNdc ||
        medication.ndc ||
        "", // "00074433902"
      gpi:
        apiResponse?.medicationPrescribed?.gpi ||
        medication.drugGpi ||
        medication.gpi ||
        "", // "66200024103020"
      strength:
        apiResponse?.medicationPrescribed?.strength ||
        program.strength ||
        medication.strength ||
        "", // "40mg/0.8ml"
      quantity: quantityVal,
      daysSupply: daysSupplyVal,
      routeOfAdministration:
        apiResponse?.medicationPrescribed?.routeOfAdministration ||
        opportunity.routeOfAdministration ||
        "",
      dosageForm:
        apiResponse?.medicationPrescribed?.dosageForm ||
        opportunity.dosageForm ||
        "",
      strengthUnitOfMeasure:
        apiResponse?.medicationPrescribed?.strengthUnitOfMeasure ||
        opportunity.strengthUnitOfMeasure ||
        "",
      priorAuthExpirationDate:
        apiResponse?.medicationPrescribed?.priorAuthExpirationDate ||
        opportunity.priorAuthExpirationDate ||
        "", // "2027-12-31"
    },
    copayAssistance: {
      iinNumber:
        apiResponse?.copayAssistance?.iinNumber ||
        copayCardDetails?.bin ||
        memberData?.copayBin ||
        opportunity.iinNumber ||
        "", // "004336"
      processorIdentificationNumber:
        apiResponse?.copayAssistance?.processorIdentificationNumber ||
        copayCardDetails?.pcn ||
        memberData?.copayPcn ||
        opportunity.processorIdentificationNumber ||
        "", // "ADV"
      groupID:
        apiResponse?.copayAssistance?.groupID ||
        copayCardDetails?.grpId ||
        memberData?.copayGrpId ||
        opportunity.copayGroupId ||
        "", // "RX1234"
      cardHolderID:
        apiResponse?.copayAssistance?.cardHolderID ||
        copayCardDetails?.memberId ||
        memberData?.copayMemberId ||
        opportunity.copayCardHolderId ||
        "", // "COPAYCARD777"
      copayAssistanceAmount: copayAssistanceAmountVal,
    },
    diagnosis: {
      description: capitalizeFirstLetter(
        apiResponse?.diagnosis?.description ||
          opportunity.diagnosisDescription ||
          "",
      ), // "Rheumatoid Arthritis"
      code: apiResponse?.diagnosis?.code || opportunity.diagnosisCode || "", // "M06.9"
    },
    prescriber: {
      npi: apiResponse?.prescriber?.npi || prescriber.npi || "", // "1982736450"
      firstName: capitalizeFirstLetter(docFirstName),
      lastName: capitalizeFirstLetter(docLastName),
      addressLine1: capitalizeFirstLetter(
        apiResponse?.prescriber?.addressLine1 || prescriber.address || "",
      ), // "456 Specialty Clinic Rd"
      addressLine2: capitalizeFirstLetter(
        apiResponse?.prescriber?.addressLine2 ||
          prescriber.addressLine2 ||
          prescriber.line2 ||
          "",
      ),
      city: capitalizeFirstLetter(
        apiResponse?.prescriber?.city || prescriber.city || "",
      ), // "Philadelphia"
      stateProvince:
        apiResponse?.prescriber?.stateProvince || prescriber.state || "", // "PA"
      postalCode:
        apiResponse?.prescriber?.postalCode || prescriber.postalCode || "", // "19104"
      phone:
        apiResponse?.prescriber?.phone ||
        formatPhoneNumber(prescriber.contact) ||
        prescriber.phone ||
        "", // "555-0155"
      fax: apiResponse?.prescriber?.fax || prescriber.fax || "", // "555-0156"
    },
  };
};

const isOpportunityReadOnly = (opportunity) =>
  !!(
    opportunity?.isSubmitted ||
    opportunity?.vendorReferenceId ||
    (opportunity?.ascellaDetails &&
      (opportunity.ascellaDetails.isSubmitted ||
        opportunity.ascellaDetails.vendorReferenceId))
  );

const AscellaEnrollmentModal = ({
  open,
  onClose,
  opportunity,
  onSubmissionSuccess,
  memberData,
}) => {
  const isModalReadOnly = isOpportunityReadOnly(opportunity);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const contentRef = useRef(null);

  // ============================================
  // Helper validation functions
  // ============================================

  const validateRequiredString = (
    value,
    fieldName,
    maxLength,
    required = true,
  ) => {
    if (!value?.trim()) {
      return required ? `${fieldName} is required.` : null;
    }
    if (maxLength && value.length > maxLength) {
      return `${fieldName} cannot exceed ${maxLength} characters.`;
    }
    return null;
  };

  const validateDate = (value, fieldName) => {
    if (!value?.trim()) return `${fieldName} is required.`;

    const dateStr = String(value).trim();
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

    if (!dateRegex.test(dateStr)) {
      return `${fieldName} must be in YYYY-MM-DD format.`;
    }

    const parsedDate = new Date(dateStr);
    if (Number.isNaN(parsedDate.getTime())) {
      return `Please enter a valid ${fieldName}.`;
    }

    return null;
  };

  const validatePhone = (value, fieldName) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 14) {
      return `${fieldName} number must be between 10 digits.`;
    }
    return null;
  };

  const validateOptionalPhone = (value, fieldName) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, "");
    if (digits.length > 14) {
      return `${fieldName} number cannot exceed 14 digits.`;
    }
    return null;
  };

  const validateNumber = (value, fieldName, options = {}) => {
    if (value === undefined || value === "") {
      return options.required ? `${fieldName} is required.` : null;
    }

    const num = Number(value);
    if (Number.isNaN(num)) {
      return `${fieldName} must be a valid number.`;
    }

    if (options.min !== undefined && num < options.min) {
      return `${fieldName} must be at least ${options.min}.`;
    }
    if (options.max !== undefined && num > options.max) {
      return `${fieldName} must be at most ${options.max}.`;
    }
    if (options.integer && !Number.isInteger(num)) {
      return `${fieldName} must be an integer.`;
    }
    if (options.positive && num <= 0) {
      return `${fieldName} must be a positive number.`;
    }

    return null;
  };

  const validateDaysSupply = (value) => {
    if (value === undefined || value === "") return null;
    const num = Number(value);
    if (Number.isNaN(num) || !Number.isInteger(num) || num <= 0 || num > 255) {
      return "Days Supply must be an integer between 1 and 255.";
    }
    return null;
  };

  const validateNPI = (value) => {
    if (!value?.trim()) return null;
    const clean = String(value).replace(/\D/g, "");
    if (clean.length !== 10) {
      return "NPI must be exactly 10 digits.";
    }
    return null;
  };

  const validateNDC = (value) => {
    if (!value?.trim()) return null;
    const str = value.trim();
    if (str.length !== 11) {
      return "NDC must be exactly 11 characters.";
    }
    return null;
  };

  const validateGPI = (value) => {
    if (!value?.trim()) return "GPI is required.";
    const str = value.trim();
    if (str.length < 4 || str.length > 14) {
      return "GPI must be between 4 and 14 characters.";
    }
    return null;
  };

  const validateEmail = (value) => {
    if (!value?.trim()) return null;
    if (value.length > 254) {
      return "Email cannot exceed 254 characters.";
    }
    if (!isValidEmail(value)) {
      return "Please enter a valid email address.";
    }
    return null;
  };

  // ============================================
  // Section-specific validation functions
  // ============================================

  const validatePatient = (patient) => {
    const errors = {};

    const firstName = validateRequiredString(
      patient?.firstName,
      "First Name",
      32,
    );
    if (firstName) errors.firstName = firstName;

    const lastName = validateRequiredString(patient?.lastName, "Last Name", 32);
    if (lastName) errors.lastName = lastName;

    const dob = validateDate(patient?.dateOfBirth, "Date of Birth");
    if (dob) errors.dateOfBirth = dob;

    const address = validateRequiredString(patient?.addressLine1, "Street", 60);
    if (address) errors.addressLine1 = address;

    const address2 = validateRequiredString(
      patient?.addressLine2,
      "Line 2",
      60,
      false,
    );
    if (address2) errors.addressLine2 = address2;

    const city = validateRequiredString(patient?.city, "City", 50);
    if (city) errors.city = city;

    const state = validateRequiredString(patient?.stateProvince, "State", 64);
    if (state) errors.stateProvince = state;

    const zip = validateRequiredString(patient?.postalCode, "Zip", 10);
    if (zip) errors.postalCode = zip;

    const country = validateRequiredString(
      patient?.countryCode,
      "Country Code",
      8,
    );
    if (country) errors.countryCode = country;

    const phone = validatePhone(patient?.phone, "Phone");
    if (phone) errors.phone = phone;

    const email = validateEmail(patient?.email);
    if (email) errors.email = email;

    return errors;
  };

  const validatePrescriber = (prescriber) => {
    const errors = {};

    // Check if any prescriber data exists
    const hasPrescriber = !!(
      prescriber?.npi?.trim() ||
      prescriber?.firstName?.trim() ||
      prescriber?.lastName?.trim() ||
      prescriber?.addressLine1?.trim() ||
      prescriber?.city?.trim() ||
      prescriber?.stateProvince?.trim() ||
      prescriber?.postalCode?.trim() ||
      prescriber?.phone?.trim() ||
      prescriber?.fax?.trim()
    );

    if (!hasPrescriber) return errors;

    const zip = validateRequiredString(prescriber?.postalCode, "Zip", 10);
    if (zip) errors.postalCode = zip;

    const npi = validateNPI(prescriber?.npi);
    if (npi) errors.npi = npi;

    const firstName = validateRequiredString(
      prescriber?.firstName,
      "First Name",
      32,
      false,
    );
    if (firstName) errors.firstName = firstName;

    const lastName = validateRequiredString(
      prescriber?.lastName,
      "Last Name",
      32,
      false,
    );
    if (lastName) errors.lastName = lastName;

    const address = validateRequiredString(
      prescriber?.addressLine1,
      "Street",
      60,
      false,
    );
    if (address) errors.addressLine1 = address;

    const address2 = validateRequiredString(
      prescriber?.addressLine2,
      "Line 2",
      60,
      false,
    );
    if (address2) errors.addressLine2 = address2;

    const city = validateRequiredString(prescriber?.city, "City", 50, false);
    if (city) errors.city = city;

    const state = validateRequiredString(
      prescriber?.stateProvince,
      "State",
      64,
      false,
    );
    if (state) errors.stateProvince = state;

    const phone = validatePhone(prescriber?.phone, "Phone");
    if (phone) errors.phone = phone;

    const fax = validateOptionalPhone(prescriber?.fax, "Fax");
    if (fax) errors.fax = fax;

    return errors;
  };

  const validateBenefitsCoordination = (benefits) => {
    const errors = {};

    const cardHolderId = validateRequiredString(
      benefits?.cardHolderID,
      "Card Holder ID",
      35,
    );
    if (cardHolderId) errors.cardHolderID = cardHolderId;

    if (!benefits?.personCode?.trim()) {
      errors.personCode = "Person Code is required.";
    } else if (benefits.personCode.trim().length !== 2) {
      errors.personCode = "Person Code must be exactly 2 characters.";
    }

    const groupId = validateRequiredString(benefits?.groupID, "Group ID", 20);
    if (groupId) errors.groupID = groupId;

    const groupName = validateRequiredString(
      benefits?.groupName,
      "Group Name",
      250,
    );
    if (groupName) errors.groupName = groupName;

    if (benefits?.benefitStartDate?.trim()) {
      const dateError = validateDate(
        benefits.benefitStartDate,
        "Benefit Start Date",
      );
      if (dateError) errors.benefitStartDate = dateError;
    }

    const phone = validatePhone(benefits?.phone, "Phone");
    if (phone) errors.phone = phone;

    return errors;
  };

  const validateCopayAssistance = (copayAssistance) => {
    const errors = {};

    if (
      copayAssistance?.iinNumber?.trim() &&
      copayAssistance.iinNumber.length > 8
    ) {
      errors.iinNumber = "IIN Number cannot exceed 8 characters.";
    }

    if (
      copayAssistance?.processorIdentificationNumber?.trim() &&
      copayAssistance.processorIdentificationNumber.length > 20
    ) {
      errors.processorIdentificationNumber =
        "Processor ID cannot exceed 20 characters.";
    }

    if (
      copayAssistance?.groupID?.trim() &&
      copayAssistance.groupID.length > 20
    ) {
      errors.groupID = "Group ID cannot exceed 20 characters.";
    }

    if (
      copayAssistance?.cardHolderID?.trim() &&
      copayAssistance.cardHolderID.length > 35
    ) {
      errors.cardHolderID = "Card Holder ID cannot exceed 35 characters.";
    }

    const amount = validateNumber(
      copayAssistance?.copayAssistanceAmount,
      "Amount",
      { required: false },
    );
    if (amount) errors.copayAssistanceAmount = amount;

    return errors;
  };

  const validateDiagnosis = (diagnosis) => {
    const errors = {};

    const hasDiagnosis = !!(
      diagnosis?.description?.trim() || diagnosis?.code?.trim()
    );

    if (!hasDiagnosis) return errors;

    const description = validateRequiredString(
      diagnosis?.description,
      "Diagnosis Description",
      150,
    );
    if (description) errors.description = description;

    const code = validateRequiredString(diagnosis?.code, "ICD Code", 15);
    if (code) errors.code = code;

    return errors;
  };

  const validateMedication = (medication) => {
    const errors = {};

    const drugName = validateRequiredString(
      medication?.drugName,
      "Drug Name",
      128,
    );
    if (drugName) errors.drugName = drugName;

    const gpi = validateGPI(medication?.gpi);
    if (gpi) errors.gpi = gpi;

    const quantity = validateNumber(medication?.quantity, "Quantity", {
      required: true,
      positive: true,
    });
    if (quantity) errors.quantity = quantity;

    const ndc = validateNDC(medication?.ndc);
    if (ndc) errors.ndc = ndc;

    if (medication?.strength?.trim() && medication.strength.length > 16) {
      errors.strength = "Strength cannot exceed 16 characters.";
    }

    const daysSupply = validateDaysSupply(medication?.daysSupply);
    if (daysSupply) errors.daysSupply = daysSupply;

    if (
      medication?.priorAuthNumber?.trim() &&
      medication.priorAuthNumber.length > 25
    ) {
      errors.priorAuthNumber = "Prior Auth Number cannot exceed 25 characters.";
    }

    if (medication?.priorAuthExpirationDate?.trim()) {
      const dateError = validateDate(
        medication.priorAuthExpirationDate,
        "PA Expiration Date",
      );
      if (dateError) errors.priorAuthExpirationDate = dateError;
    }

    return errors;
  };

  // ============================================
  // Main validation function
  // ============================================

  const validateFields = useCallback((data) => {
    if (!data) return {};

    const sectionErrors = {
      patient: validatePatient(data.patient),
      prescriber: validatePrescriber(data.prescriber),
      benefitsCoordination: validateBenefitsCoordination(
        data.benefitsCoordination,
      ),
      copayAssistance: validateCopayAssistance(data.copayAssistance),
      diagnosis: validateDiagnosis(data.diagnosis),
      medicationPrescribed: validateMedication(data.medicationPrescribed),
    };

    return Object.fromEntries(
      Object.entries(sectionErrors).filter(
        ([, errs]) => Object.keys(errs).length > 0,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && opportunity) {
      setErrorMessage(""); // Reset error message on open
      const memberProgramId =
        opportunity?.memberProgramId ||
        opportunity?.opportunityId ||
        opportunity?.id;

      setIsLoadingForm(true);

      let referralPromise;
      if (opportunity?.ascellaDetails) {
        referralPromise = Promise.resolve(opportunity.ascellaDetails);
      } else if (memberProgramId) {
        referralPromise = prepareAscellaReferral(memberProgramId);
      } else {
        referralPromise = Promise.resolve({});
      }

      const copayPromise = Promise.resolve({});

      Promise.allSettled([referralPromise, copayPromise])
        .then(([referralRes, copayRes]) => {
          const referralData =
            referralRes.status === "fulfilled" ? referralRes.value : {};
          const copayData =
            copayRes.status === "fulfilled" ? copayRes.value : {};
          const initialData = getInitialFormData(
            opportunity,
            referralData,
            memberData,
            copayData,
          );
          setFormData(initialData);
          setFormErrors(validateFields(initialData));
        })
        .catch((err) => {
          console.error("Error setting up ascella referral form:", err);
          const initialData = getInitialFormData(
            opportunity,
            {},
            memberData,
            {},
          );
          setFormData(initialData);
          setFormErrors(validateFields(initialData));
        })
        .finally(() => {
          setIsLoadingForm(false);
        });

      setActiveStep(0);
    }
  }, [open, opportunity, memberData, validateFields, isModalReadOnly]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeStep]);

  const currentStepHasErrors = useMemo(() => {
    if (isModalReadOnly) return false;
    if (activeStep === 0)
      return !!(formErrors.patient || formErrors.prescriber);
    if (activeStep === 1)
      return !!(
        formErrors.benefitsCoordination ||
        formErrors.copayAssistance ||
        formErrors.diagnosis
      );
    if (activeStep === 2) return !!formErrors.medicationPrescribed;
    return false;
  }, [activeStep, formErrors, isModalReadOnly]);

  const handleFieldChange = useCallback(
    (section, field, value) => {
      setFormData((prev) => {
        const newData = {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value,
          },
        };
        setFormErrors(validateFields(newData));
        return newData;
      });
    },
    [validateFields],
  );

  // ============================================
  // Payload builder helper functions
  // ============================================

  const mapPayloadGender = (g) => {
    if (!g) return undefined;
    const upper = String(g).trim().toUpperCase();
    if (upper === "MALE" || upper === "M") return "Male";
    if (upper === "FEMALE" || upper === "F") return "Female";
    return undefined;
  };

  const cleanPhoneNumber = (phone) => {
    if (!phone?.trim()) return undefined;
    return String(phone).replace(/\D/g, "").slice(0, 14);
  };

  const parseNumber = (value) => {
    if (value === undefined || value === "") return undefined;
    return parseDouble(value);
  };

  const parseIntValue = (value) => {
    if (value === undefined || value === "") return undefined;
    return Number.parseInt(value, 10);
  };

  const formatDateIfPresent = (date) => {
    if (!date?.trim()) return undefined;
    return formatISO(date);
  };

  const trimIfPresent = (value) => {
    if (!value?.trim()) return undefined;
    return value.trim();
  };

  // ============================================
  // Section-specific payload builders
  // ============================================

  const buildPatientPayload = (patient) => {
    const payload = {
      firstName: patient.firstName?.trim() || "",
      lastName: patient.lastName?.trim() || "",
      dateOfBirth: formatISO(patient.dateOfBirth),
      addressLine1: patient.addressLine1?.trim() || "",
      addressLine2: patient.addressLine2?.trim() || "",
      city: patient.city?.trim() || "",
      stateProvince: patient.stateProvince?.trim() || "",
      postalCode: patient.postalCode?.trim() || "",
      countryCode: patient.countryCode?.trim() || "US",
    };

    const gender = mapPayloadGender(patient.gender);
    if (gender) payload.gender = gender;

    const phone = cleanPhoneNumber(patient.phone);
    if (phone) payload.phone = phone;

    const email = trimIfPresent(patient.email);
    if (email) payload.email = email;

    if (patient.phoneSupportsSms !== undefined) {
      payload.phoneSupportsSms = patient.phoneSupportsSms;
    }

    return payload;
  };

  const buildBenefitsPayload = (benefits) => {
    const payload = {
      cardHolderID: benefits.cardHolderID?.trim() || "",
      personCode: String(benefits.personCode || "").trim(),
      groupID: benefits.groupID?.trim() || "",
      groupName: benefits.groupName?.trim() || "",
    };

    const iinNumber = trimIfPresent(benefits.iinNumber);
    if (iinNumber) payload.iinNumber = iinNumber;

    const processorId = trimIfPresent(benefits.processorIdentificationNumber);
    if (processorId) payload.processorIdentificationNumber = processorId;

    const startDate = formatDateIfPresent(benefits.benefitStartDate);
    if (startDate) payload.benefitStartDate = startDate;

    const phone = cleanPhoneNumber(benefits.phone);
    if (phone) payload.phone = phone;

    return payload;
  };

  const buildMedicationPayload = (medication) => {
    const payload = {
      drugName: medication.drugName?.trim() || "",
      gpi: medication.gpi?.trim() || "",
      quantity: parseDouble(medication.quantity) || 0,
    };

    const rxNumber = trimIfPresent(medication.rxNumber);
    if (rxNumber) payload.rxNumber = rxNumber;

    const priorAuthNumber = trimIfPresent(medication.priorAuthNumber);
    if (priorAuthNumber) payload.priorAuthNumber = priorAuthNumber;

    const writtenDate = formatDateIfPresent(medication.writtenDate);
    if (writtenDate) payload.writtenDate = writtenDate;

    const ndc = trimIfPresent(medication.ndc);
    if (ndc) payload.ndc = ndc;

    const strength = trimIfPresent(medication.strength);
    if (strength) payload.strength = strength;

    const daysSupply = parseIntValue(medication.daysSupply);
    if (daysSupply !== undefined) payload.daysSupply = daysSupply;

    const unit = trimIfPresent(medication.strengthUnitOfMeasure);
    if (unit) payload.strengthUnitOfMeasure = unit;

    const paExpiration = formatDateIfPresent(
      medication.priorAuthExpirationDate,
    );
    if (paExpiration) payload.priorAuthExpirationDate = paExpiration;

    return payload;
  };

  const buildCopayAssistancePayload = (copayAssistance) => {
    const hasCopayAssistance = !!(
      copayAssistance?.iinNumber?.trim() &&
      copayAssistance?.processorIdentificationNumber?.trim() &&
      copayAssistance?.groupID?.trim() &&
      copayAssistance?.cardHolderID?.trim() &&
      copayAssistance?.copayAssistanceAmount !== undefined &&
      copayAssistance?.copayAssistanceAmount !== ""
    );

    if (!hasCopayAssistance) return null;

    const payload = {};

    const iinNumber = trimIfPresent(copayAssistance.iinNumber);
    if (iinNumber) payload.iinNumber = iinNumber;

    const processorId = trimIfPresent(
      copayAssistance.processorIdentificationNumber,
    );
    if (processorId) payload.processorIdentificationNumber = processorId;

    const groupId = trimIfPresent(copayAssistance.groupID);
    if (groupId) payload.groupID = groupId;

    const cardHolderId = trimIfPresent(copayAssistance.cardHolderID);
    if (cardHolderId) payload.cardHolderID = cardHolderId;

    const amount = parseNumber(copayAssistance.copayAssistanceAmount);
    if (amount !== undefined) payload.copayAssistanceAmount = amount;

    return payload;
  };

  const buildDiagnosisPayload = (diagnosis) => {
    const hasDiagnosis = !!(
      diagnosis?.description?.trim() || diagnosis?.code?.trim()
    );

    if (!hasDiagnosis) return null;

    return {
      description: diagnosis.description?.trim() || "",
      code: diagnosis.code?.trim() || "",
    };
  };

  const buildPrescriberPayload = (prescriber) => {
    const hasPrescriber = !!(
      prescriber?.npi?.trim() ||
      prescriber?.firstName?.trim() ||
      prescriber?.lastName?.trim() ||
      prescriber?.addressLine1?.trim() ||
      prescriber?.city?.trim() ||
      prescriber?.stateProvince?.trim() ||
      prescriber?.postalCode?.trim() ||
      prescriber?.phone?.trim() ||
      prescriber?.fax?.trim()
    );

    if (!hasPrescriber) return null;

    const payload = {
      postalCode: prescriber.postalCode?.trim() || "",
    };

    const npi = trimIfPresent(prescriber.npi);
    if (npi) payload.npi = npi;

    const firstName = trimIfPresent(prescriber.firstName);
    if (firstName) payload.firstName = firstName;

    const lastName = trimIfPresent(prescriber.lastName);
    if (lastName) payload.lastName = lastName;

    const address1 = trimIfPresent(prescriber.addressLine1);
    if (address1) payload.addressLine1 = address1;

    const address2 = trimIfPresent(prescriber.addressLine2);
    if (address2) payload.addressLine2 = address2;

    const city = trimIfPresent(prescriber.city);
    if (city) payload.city = city;

    const state = trimIfPresent(prescriber.stateProvince);
    if (state) payload.stateProvince = state;

    const phone = cleanPhoneNumber(prescriber.phone);
    if (phone) payload.phone = phone;

    const fax = cleanPhoneNumber(prescriber.fax);
    if (fax) payload.fax = fax;

    return payload;
  };

  // ============================================
  // Error handling helper
  // ============================================

  const extractErrorMessages = (errors) => {
    const errorMessages = [];
    for (const key of Object.keys(errors)) {
      if (Array.isArray(errors[key])) {
        errorMessages.push(...errors[key]);
      } else if (typeof errors[key] === "string") {
        errorMessages.push(errors[key]);
      }
    }
    return errorMessages.length > 0 ? errorMessages.join(" ") : null;
  };

  const tryParseNestedJson = (msg) => {
    if (typeof msg !== "string" || msg.trim() === "") return null;

    const jsonStart = msg.indexOf("{");
    if (jsonStart === -1) return msg;

    try {
      const parsed = JSON.parse(msg.substring(jsonStart));
      if (parsed.errors) {
        const errStr = extractErrorMessages(parsed.errors);
        if (errStr) return errStr;
      }
      return parsed.detail || parsed.message || parsed.title || msg;
    } catch (e) {
      console.error("Failed to parse nested error JSON:", e);
      return msg;
    }
  };

  const handleSubmitError = (error) => {
    console.error(error);
    if (error?.response?.status === 429) {
      return "Ascella services are currently busy. Please try submitting again in a few moments.";
    }

    // 1. Try from response payload
    const responseData = error?.response?.data;
    if (responseData) {
      const rawMsg = responseData.message || responseData.error;
      const parsed = tryParseNestedJson(rawMsg);
      if (parsed) return parsed;
    }

    // 2. Try from error.message direct
    if (error?.message) {
      const parsed = tryParseNestedJson(error.message);
      if (parsed) return parsed;
    }

    return "An unexpected error occurred while submitting to Ascella. Please try again. If the issue persists, contact your system administrator.";
  };

  // ============================================
  // Main handleSubmit function
  // ============================================

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(""); // Reset previous error

    try {
      const memberProgramId =
        opportunity?.memberProgramId ||
        opportunity?.opportunityId ||
        opportunity?.id ||
        "12345";

      const finalPayload = {
        patient: buildPatientPayload(formData.patient),
        benefitsCoordination: buildBenefitsPayload(
          formData.benefitsCoordination,
        ),
        medicationPrescribed: buildMedicationPayload(
          formData.medicationPrescribed,
        ),
      };

      if (formData.clientAccountId) {
        finalPayload.clientAccountId = String(formData.clientAccountId);
      }

      const copayPayload = buildCopayAssistancePayload(
        formData.copayAssistance,
      );
      if (copayPayload) {
        finalPayload.copayAssistance = copayPayload;
      }

      const diagnosisPayload = buildDiagnosisPayload(formData.diagnosis);
      if (diagnosisPayload) {
        finalPayload.diagnosis = diagnosisPayload;
      }

      const prescriberPayload = buildPrescriberPayload(formData.prescriber);
      if (prescriberPayload) {
        finalPayload.prescriber = prescriberPayload;
      }

      const result = await submitAscellaReferral(memberProgramId, finalPayload);

      if (result) {
        setSuccessModalOpen(true);
      } else {
        setErrorMessage(
          "Referral submission completed, but returned empty response.",
        );
      }
    } catch (error) {
      const errorMessage = handleSubmitError(error);
      setErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={isLoadingForm ? undefined : onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "90vw",
              lg: "80vw",
              xl: "75vw",
            },
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
          },
        },
      }}
    >
      {isLoadingForm ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 12,
            gap: 2,
          }}
        >
          <CircularProgress size={50} sx={{ color: "#403C6F" }} />
          <Typography
            sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
          >
            Preparing Referral Data...
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: "relative",
              p: 2,
              display: "flex",
              flexDirection: "column",
              height: "100%",
              maxHeight: "90vh",
            }}
          >
            {/* Header Title */}
            <DialogTitle
              sx={{
                p: 0,
                m: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "20px",
                  color: "#10100F",
                  flexShrink: 0,
                }}
              >
                Ascella Submission - Step {activeStep + 1} of {steps.length}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  ml: 2,
                  flex: 1,
                  justifyContent: "flex-end",
                  minWidth: 0,
                }}
              >
                {errorMessage && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "13px",
                      fontWeight: 500,
                      lineHeight: 1.4,
                      textAlign: "center",
                      maxWidth: "600px",
                      wordBreak: "break-word",
                    }}
                  >
                    {errorMessage}
                  </Typography>
                )}
                <IconButton
                  onClick={onClose}
                  sx={{ color: "#403C6F" }}
                  size="small"
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            {/* Member/Opportunity Header Card */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}
            >
              <AscellaMemberDetailsHeader opportunity={opportunity} />

              {/* Stepper */}
              <Box sx={{ width: "95%", mx: "auto" }}>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: 500,
                      fontSize: "13px",
                      color: "#A09EBA",
                      marginTop: "6px",
                    },
                    "& .MuiStepLabel-label.Mui-active": {
                      color: "#403C6F",
                      fontWeight: 600,
                    },
                    "& .MuiStepLabel-iconContainer": {
                      padding: 0,
                    },
                    "& .MuiStepConnector-root": {
                      top: "6px",
                      left: "calc(-50% + 7px)",
                      right: "calc(50% + 7px)",
                    },
                    "& .MuiStepConnector-line": {
                      borderColor: "#C4C2D3",
                      borderTopWidth: 2,
                    },
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel slots={{ stepIcon: CustomStepIcon }}>
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DialogContent
                ref={contentRef}
                sx={{
                  p: 0,
                  pb: 2,
                  backgroundColor: "transparent",
                  overflowX: "hidden",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#D8D6E2",
                    borderRadius: "4px",
                  },
                }}
              >
                <Box sx={{ flex: 1, minHeight: "350px", pr: 1 }}>
                  {/* STEP 1: PATIENT & PRESCRIBER INFO */}
                  {activeStep === 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "stretch",
                      }}
                    >
                      {/* Patient Card */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Card
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #E8E8E9",
                            boxShadow: "none",
                            height: "100%",
                          }}
                        >
                          <PatientDemographics
                            data={formData.patient}
                            onChange={(field, value) =>
                              handleFieldChange("patient", field, value)
                            }
                            errors={isModalReadOnly ? {} : formErrors.patient}
                            disabled={isModalReadOnly}
                          />
                        </Card>
                      </Box>

                      {/* Prescriber Card */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Card
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #E8E8E9",
                            boxShadow: "none",
                            height: "100%",
                          }}
                        >
                          <PrescriberInfo
                            data={formData.prescriber}
                            onChange={(field, value) =>
                              handleFieldChange("prescriber", field, value)
                            }
                            errors={
                              isModalReadOnly ? {} : formErrors.prescriber
                            }
                            disabled={isModalReadOnly}
                          />
                        </Card>
                      </Box>
                    </Box>
                  )}

                  {/* STEP 2: INSURANCE & COPAY ASSISTANCE */}
                  {activeStep === 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 3,
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "stretch",
                      }}
                    >
                      {/* Benefits Coordination */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Card
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #E8E8E9",
                            boxShadow: "none",
                            height: "100%",
                          }}
                        >
                          <BenefitsCoordination
                            data={formData.benefitsCoordination}
                            onChange={(field, value) =>
                              handleFieldChange(
                                "benefitsCoordination",
                                field,
                                value,
                              )
                            }
                            errors={
                              isModalReadOnly
                                ? {}
                                : formErrors.benefitsCoordination
                            }
                            disabled={isModalReadOnly}
                          />
                        </Card>
                      </Box>

                      {/* Copay Assistance & Diagnosis */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Card
                          sx={{
                            borderRadius: "12px",
                            border: "1px solid #E8E8E9",
                            boxShadow: "none",
                            height: "100%",
                          }}
                        >
                          <CopayAssistanceAndDiagnosis
                            copayData={formData.copayAssistance}
                            diagnosisData={formData.diagnosis}
                            copayDisabled={false}
                            onCopayChange={(field, value) =>
                              handleFieldChange("copayAssistance", field, value)
                            }
                            onDiagnosisChange={(field, value) =>
                              handleFieldChange("diagnosis", field, value)
                            }
                            errors={isModalReadOnly ? {} : formErrors}
                            disabled={isModalReadOnly}
                          />
                        </Card>
                      </Box>
                    </Box>
                  )}

                  {/* STEP 3: MEDICATION INFO */}
                  {activeStep === 2 && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
                    >
                      <Card
                        sx={{
                          borderRadius: "12px",
                          border: "1px solid #E8E8E9",
                          boxShadow: "none",
                        }}
                      >
                        <MedicationPrescribed
                          data={formData.medicationPrescribed}
                          onChange={(field, value) =>
                            handleFieldChange(
                              "medicationPrescribed",
                              field,
                              value,
                            )
                          }
                          errors={
                            isModalReadOnly
                              ? {}
                              : formErrors.medicationPrescribed
                          }
                          disabled={isModalReadOnly}
                        />
                      </Card>
                    </Box>
                  )}
                </Box>
              </DialogContent>

              {/* Footer Actions */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  pt: 2,
                  pb: 2,
                  px: 3,
                  mx: -2,
                  mb: -2,
                  mt: "auto",
                  backgroundColor: "#fff",
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {activeStep !== 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setActiveStep((prev) => prev - 1);
                      setErrorMessage("");
                    }}
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: "50px",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "14px",
                      color: "#403C6F",
                      borderColor: "#9F9DB6",
                      px: 3,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "rgba(64, 60, 111, 0.04)",
                        borderColor: "#403C6F",
                      },
                    }}
                  >
                    Back
                  </Button>
                )}

                <Button
                  variant="contained"
                  onClick={() => {
                    const isFinalStep = activeStep === steps.length - 1;
                    if (isFinalStep) {
                      void handleSubmit();
                    } else {
                      setActiveStep((prev) => prev + 1);
                      setErrorMessage("");
                    }
                  }}
                  disabled={
                    (activeStep === steps.length - 1 && isModalReadOnly) ||
                    isSubmitting ||
                    (!isModalReadOnly && currentStepHasErrors)
                  }
                  sx={{
                    borderRadius: "50px",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "14px",
                    backgroundColor: "#403C6F",
                    color: "#FFF",
                    px: 4,
                    py: 1,
                    boxShadow: "none",
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(64, 60, 111, 0.4)",
                      color: "rgba(255, 255, 255, 0.7)",
                    },
                    "&:hover": {
                      backgroundColor: "#312e5e",
                      boxShadow: "none",
                    },
                  }}
                >
                  {(() => {
                    if (activeStep !== steps.length - 1) {
                      return "Next";
                    }
                    return isSubmitting ? "Submitting..." : "Submit to Ascella";
                  })()}
                </Button>
              </Box>
            </LocalizationProvider>
          </Box>
          {/* Submission Failed popup dialog is removed to display errors inline at top-right instead */}

          <CommonDialog
            open={successModalOpen}
            onClose={() => {
              setSuccessModalOpen(false);
              if (onSubmissionSuccess) {
                onSubmissionSuccess(
                  opportunity?.memberProgramId ||
                    opportunity?.opportunityId ||
                    opportunity?.id ||
                    "12345",
                );
              }
              onClose();
            }}
            variant="alert"
            icon={<CheckIcon sx={{ color: "#137333", fontSize: "32px" }} />}
            iconBgColor="#E6F4EA"
            title="Order Submitted"
            actions={
              <Button
                variant="contained"
                onClick={() => {
                  setSuccessModalOpen(false);
                  if (onSubmissionSuccess) {
                    onSubmissionSuccess(
                      opportunity?.memberProgramId ||
                        opportunity?.opportunityId ||
                        opportunity?.id ||
                        "12345",
                    );
                  }
                  onClose();
                }}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  backgroundColor: "#403C6F",
                  color: "#FFF",
                  px: 6,
                  py: 1,
                  boxShadow: "none",
                  width: "100%",
                  maxWidth: "200px",
                  "&:hover": {
                    backgroundColor: "#312e5e",
                    boxShadow: "none",
                  },
                }}
              >
                Close
              </Button>
            }
          >
            The enrollment request has been successfully submitted to Ascella
            for processing.
          </CommonDialog>
        </>
      )}
    </Dialog>
  );
};

AscellaEnrollmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  opportunity: PropTypes.object,
  onSubmissionSuccess: PropTypes.func,
  memberData: PropTypes.object,
};

export default AscellaEnrollmentModal;
